// AllMemberManagement.js - 全國{MEMBER_TERM}資訊管理 (簡化高效版)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Badge, InputGroup, Button, Modal, Form, Spinner, Alert, Table } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isSuperAdmin } from './utils/superAdmin';
import PhotoUpload from './services/PhotoUpload';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import {
  downloadEmployeesExcel,
  handleExcelFileUpload,
  uploadEmployeesData,
  previewEmployeeChanges,
  formatImportResults
} from './utils/employeeExcel';
import { MEMBER_TERM } from './config/clubTerms';

const AllMemberManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // 基本狀態
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('0'); // 0=啟用, 1=停用, all=全部
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  // 下拉選單選項
  const [allStores, setAllStores] = useState([]);
  const [employeeGroups, setEmployeeGroups] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);

  // 編輯 Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  // 訊息
  const [message, setMessage] = useState({ type: '', text: '' });

  // Lightbox
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // 匯入匯出
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // 無限滾動
  const observer = useRef();
  const lastEmployeeRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // 權限檢查（超級管理員）
  useEffect(() => {
    if (!user || !isSuperAdmin(user.e_id)) {
      navigate('/');
    }
  }, [user, navigate]);

  // 載入下拉選單資料
  useEffect(() => {
    const loadOptions = async () => {
      // 分別載入各選項，避免一個失敗影響全部
      try {
        const storesRes = await axios.get('/stores');
        setAllStores(storesRes.data || []);
      } catch (error) {
        console.error('載入社別失敗:', error);
      }

      try {
        const groupsRes = await axios.get('/employee-groups');
        setEmployeeGroups(groupsRes.data || []);
      } catch (error) {
        console.error('載入群組失敗:', error);
      }

      try {
        const positionsRes = await axios.get('/positions');
        setPositionOptions(positionsRes.data || []);
      } catch (error) {
        console.error('載入職位失敗:', error);
      }
    };
    loadOptions();
  }, []);

  // 載入員工資料
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '50',
          sortKey: 'e_id',
          sortDirection: 'asc'
        });

        if (searchQuery) params.append('filter', searchQuery);

        const response = await axios.get(`/employees?${params.toString()}`);
        const newEmployees = response.data.data || [];

        if (page === 1) {
          setEmployees(newEmployees);
        } else {
          setEmployees(prev => [...prev, ...newEmployees]);
        }

        setTotalRecords(response.data.totalRecords || 0);
        setHasMore(response.data.hasMore || false);
      } catch (error) {
        console.error('載入員工失敗:', error);
        setMessage({ type: 'danger', text: '載入資料失敗' });
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [page, searchQuery]);

  // 搜尋處理 (防抖)
  const searchTimeoutRef = useRef(null);
  const handleSearch = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
      setEmployees([]);
    }, 300);
  };

  // 篩選處理 (客戶端篩選)
  const handleFilterChange = (type, value) => {
    if (type === 'store') setStoreFilter(value);
    if (type === 'status') setStatusFilter(value);
  };

  // 客戶端篩選員工列表
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      // 社別篩選
      if (storeFilter && emp.e_ssid !== parseInt(storeFilter)) return false;
      // 狀態篩選
      if (statusFilter !== 'all' && emp.e_status !== parseInt(statusFilter)) return false;
      return true;
    });
  }, [employees, storeFilter, statusFilter]);

  // 開啟編輯 Modal
  const handleEdit = async (employee) => {
    try {
      setLoading(true);
      const response = await axios.get(`/employees/${employee.e_id}`);
      setSelectedEmployee(response.data);
      setShowEditModal(true);
    } catch (error) {
      setMessage({ type: 'danger', text: '載入員工資料失敗' });
    } finally {
      setLoading(false);
    }
  };

  // 儲存編輯
  const handleSave = async () => {
    if (!selectedEmployee) return;

    setSaving(true);
    try {
      await axios.put(`/employees/${selectedEmployee.e_id}`, selectedEmployee);
      setMessage({ type: 'success', text: '儲存成功！' });

      // 更新列表中的資料
      setEmployees(prev => prev.map(emp =>
        emp.e_id === selectedEmployee.e_id
          ? { ...emp, ...selectedEmployee }
          : emp
      ));

      setShowEditModal(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'danger', text: '儲存失敗：' + (error.response?.data?.message || error.message) });
    } finally {
      setSaving(false);
    }
  };

  // 刪除員工
  const handleDelete = async (employee) => {
    if (!window.confirm(`確定要刪除 ${employee.e_name} 嗎？`)) return;

    try {
      await axios.delete(`/employees/${employee.e_id}`);
      setEmployees(prev => prev.filter(emp => emp.e_id !== employee.e_id));
      setMessage({ type: 'success', text: '刪除成功！' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'danger', text: '刪除失敗' });
    }
  };

  // 匯出 Excel
  const handleExport = async () => {
    try {
      setLoading(true);
      await downloadEmployeesExcel(storeFilter || 'all');
      setMessage({ type: 'success', text: '匯出成功！' });
    } catch (error) {
      setMessage({ type: 'danger', text: '匯出失敗' });
    } finally {
      setLoading(false);
    }
  };

  // 匯入 Excel
  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await handleExcelFileUpload(file);
      setImportData(data.data);

      // 預覽變更
      const preview = await previewEmployeeChanges(data.data);
      setImportPreview(preview);
      setShowImportModal(true);
    } catch (error) {
      setMessage({ type: 'danger', text: '讀取檔案失敗：' + error.message });
    }

    e.target.value = '';
  };

  // 確認匯入
  const handleConfirmImport = async () => {
    if (!importData) return;

    setImporting(true);
    try {
      const result = await uploadEmployeesData(importData);
      setMessage({ type: 'success', text: formatImportResults(result) });
      setShowImportModal(false);
      setImportData(null);
      setImportPreview(null);

      // 重新載入資料
      setPage(1);
      setEmployees([]);
    } catch (error) {
      setMessage({ type: 'danger', text: '匯入失敗：' + error.message });
    } finally {
      setImporting(false);
    }
  };

  // 更新欄位值
  const updateField = (field, value) => {
    setSelectedEmployee(prev => ({ ...prev, [field]: value }));
  };

  // 性別選項
  const sexOptions = [
    { value: '1', label: '男' },
    { value: '2', label: '女' }
  ];

  // 衣服尺寸選項
  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  // 飲食習慣選項
  const foodTypeOptions = [
    { value: '0', label: '葷食' },
    { value: '1', label: '素食' },
    { value: '2', label: '海鮮過敏' },
    { value: '3', label: '其他' }
  ];

  if (!user || !isSuperAdmin(user.e_id)) {
    return <Container className="mt-4"><Alert variant="danger">權限不足</Alert></Container>;
  }

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* 訊息提示 */}
      {message.text && (
        <Alert
          variant={message.type}
          dismissible
          onClose={() => setMessage({ type: '', text: '' })}
          className="position-fixed"
          style={{ top: '70px', right: '20px', zIndex: 9999, minWidth: '300px' }}
        >
          {message.text}
        </Alert>
      )}

      {/* 標題和工具列 */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={4}>
              <h4 className="mb-0">
                <span className="me-2">👥</span>
                全國{MEMBER_TERM}資訊管理
                <Badge bg="secondary" className="ms-2">
                  {(storeFilter || statusFilter !== '0') ? `${filteredEmployees.length}/${employees.length}` : `${employees.length}`} 筆
                </Badge>
              </h4>
            </Col>
            <Col md={8}>
              <Row className="g-2">
                {/* 搜尋 */}
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="搜尋姓名、手機、Email..."
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                {/* 社別篩選 */}
                <Col md={2}>
                  <Form.Select
                    value={storeFilter}
                    onChange={(e) => handleFilterChange('store', e.target.value)}
                  >
                    <option value="">全部社別</option>
                    {allStores.map(store => (
                      <option key={store.ss_id} value={store.ss_id}>{store.ss_name}</option>
                    ))}
                  </Form.Select>
                </Col>
                {/* 狀態篩選 */}
                <Col md={2}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="0">啟用中</option>
                    <option value="1">已停用</option>
                    <option value="all">全部</option>
                  </Form.Select>
                </Col>
                {/* 匯入匯出按鈕 */}
                <Col md={4} className="text-end">
                  <Button variant="success" className="me-2" onClick={handleExport} disabled={loading}>
                    📥 匯出 Excel
                  </Button>
                  <Button variant="primary" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                    📤 匯入 Excel
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".xlsx,.xls"
                    onChange={handleImportFile}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 員工列表 */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead style={{ backgroundColor: '#e9ecef' }}>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th style={{ width: '60px' }}>照片</th>
                <th style={{ width: '80px' }}>姓名</th>
                <th style={{ width: '80px' }}>社別</th>
                <th style={{ width: '100px' }}>手機</th>
                <th style={{ width: '60px' }}>狀態</th>
                <th style={{ width: '100px' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee, index) => (
                <tr
                  key={employee.e_id}
                  ref={index === filteredEmployees.length - 1 ? lastEmployeeRef : null}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEdit(employee)}
                >
                  <td>{employee.e_id}</td>
                  <td>
                    {employee.e_photo ? (
                      <img
                        src={`/uploads/member/${employee.e_photo}`}
                        alt={employee.e_name}
                        style={{ width: '40px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxImage(`/uploads/member/${employee.e_photo}`);
                          setIsLightboxOpen(true);
                        }}
                      />
                    ) : (
                      <div style={{ width: '40px', height: '50px', backgroundColor: '#dee2e6', borderRadius: '4px' }} />
                    )}
                  </td>
                  <td><strong>{employee.e_name}</strong></td>
                  <td>{employee.store_name}</td>
                  <td>{employee.e_mobile}</td>
                  <td>
                    <Badge bg={employee.e_status === 0 ? 'success' : 'danger'}>
                      {employee.e_status === 0 ? '啟用' : '停用'}
                    </Badge>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleEdit(employee)}
                    >
                      編輯
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(employee)}
                    >
                      刪除
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">載入中...</p>
            </div>
          )}

          {!loading && employees.length === 0 && (
            <div className="text-center py-5 text-muted">
              <h5>找不到符合條件的資料</h5>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* 編輯 Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        scrollable
      >
        <Modal.Header closeButton style={{ backgroundColor: '#0d6efd', color: 'white' }}>
          <Modal.Title>
            編輯{MEMBER_TERM}資料 - {selectedEmployee?.e_name} (ID: {selectedEmployee?.e_id})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedEmployee && (
            <>
              {/* 基本資訊 */}
              <Card className="mb-3">
                <Card.Header className="bg-primary text-white py-2">
                  <h6 className="mb-0">📋 基本資訊</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3} className="text-center mb-3">
                      <PhotoUpload
                        currentPhoto={selectedEmployee.e_photo}
                        employeeId={selectedEmployee.e_id}
                        onPhotoChange={(photo) => updateField('e_photo', photo)}
                      />
                    </Col>
                    <Col md={9}>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">姓名 *</Form.Label>
                            <Form.Control
                              size="sm"
                              value={selectedEmployee.e_name || ''}
                              onChange={(e) => updateField('e_name', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">社別</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedEmployee.e_ssid || ''}
                              onChange={(e) => updateField('e_ssid', e.target.value)}
                            >
                              <option value="">選擇社別</option>
                              {allStores.map(store => (
                                <option key={store.ss_id} value={store.ss_id}>{store.ss_name}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">性別</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedEmployee.e_sex || ''}
                              onChange={(e) => updateField('e_sex', e.target.value)}
                            >
                              <option value="">選擇性別</option>
                              {sexOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">生日</Form.Label>
                            <Form.Control
                              size="sm"
                              type="date"
                              value={selectedEmployee.e_birthday || ''}
                              onChange={(e) => updateField('e_birthday', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">部門</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedEmployee.e_group || ''}
                              onChange={(e) => updateField('e_group', e.target.value)}
                            >
                              <option value="">選擇部門</option>
                              {employeeGroups.map(grp => (
                                <option key={grp.g_id} value={grp.g_id}>{grp.g_name}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">社職</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedEmployee.e_position || ''}
                              onChange={(e) => updateField('e_position', e.target.value)}
                            >
                              <option value="">選擇職位</option>
                              {positionOptions.map(pos => (
                                <option key={pos.s_id} value={pos.s_id}>{pos.s_name}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">狀態</Form.Label>
                            <Form.Select
                              size="sm"
                              value={selectedEmployee.e_status?.toString() || '0'}
                              onChange={(e) => updateField('e_status', parseInt(e.target.value))}
                            >
                              <option value="0">啟用</option>
                              <option value="1">停用</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">入社日期</Form.Label>
                            <Form.Control
                              size="sm"
                              type="date"
                              value={selectedEmployee.e_join_date || ''}
                              onChange={(e) => updateField('e_join_date', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">退社日期</Form.Label>
                            <Form.Control
                              size="sm"
                              type="date"
                              value={selectedEmployee.e_quit_date || ''}
                              onChange={(e) => updateField('e_quit_date', e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* 聯絡資訊 */}
              <Card className="mb-3">
                <Card.Header className="bg-success text-white py-2">
                  <h6 className="mb-0">📞 聯絡資訊</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">手機 *</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_mobile || ''}
                          onChange={(e) => updateField('e_mobile', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">LINE ID</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_line_id || ''}
                          onChange={(e) => updateField('e_line_id', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Email</Form.Label>
                        <Form.Control
                          size="sm"
                          type="email"
                          value={selectedEmployee.e_email || ''}
                          onChange={(e) => updateField('e_email', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">家用電話</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_tel || ''}
                          onChange={(e) => updateField('e_tel', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司電話</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_tel1 || ''}
                          onChange={(e) => updateField('e_tel1', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">傳真</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_fax || ''}
                          onChange={(e) => updateField('e_fax', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">住家地址</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_address || ''}
                          onChange={(e) => updateField('e_address', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司地址</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_address1 || ''}
                          onChange={(e) => updateField('e_address1', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">通訊地址</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_address2 || ''}
                          onChange={(e) => updateField('e_address2', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* 公司資訊 */}
              <Card className="mb-3">
                <Card.Header className="bg-warning py-2">
                  <h6 className="mb-0">🏢 公司資訊</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司名稱</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_comp_name || ''}
                          onChange={(e) => updateField('e_comp_name', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司職位</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_comp_position || ''}
                          onChange={(e) => updateField('e_comp_position', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司網站</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_comp_web || ''}
                          onChange={(e) => updateField('e_comp_web', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">營業項目</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.sql_title4 || ''}
                          onChange={(e) => updateField('sql_title4', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">公司簡介</Form.Label>
                        <Form.Control
                          size="sm"
                          as="textarea"
                          rows={2}
                          value={selectedEmployee.e_comp_open || ''}
                          onChange={(e) => updateField('e_comp_open', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* 其他資訊 */}
              <Card className="mb-3">
                <Card.Header className="bg-info text-white py-2">
                  <h6 className="mb-0">📝 其他資訊</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">結婚紀念日</Form.Label>
                        <Form.Control
                          size="sm"
                          type="date"
                          value={selectedEmployee.e_marry || ''}
                          onChange={(e) => updateField('e_marry', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">衣服尺寸</Form.Label>
                        <Form.Select
                          size="sm"
                          value={selectedEmployee.e_size || ''}
                          onChange={(e) => updateField('e_size', e.target.value)}
                        >
                          <option value="">選擇尺寸</option>
                          {sizeOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">飲食習慣</Form.Label>
                        <Form.Select
                          size="sm"
                          value={selectedEmployee.e_foodType || ''}
                          onChange={(e) => updateField('e_foodType', e.target.value)}
                        >
                          <option value="">選擇</option>
                          {foodTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">籍貫</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.sql_title1 || ''}
                          onChange={(e) => updateField('sql_title1', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">學歷</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.sql_title2 || ''}
                          onChange={(e) => updateField('sql_title2', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">社團經歷</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.sql_title5 || ''}
                          onChange={(e) => updateField('sql_title5', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Golf 差點</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.sql_title6 || ''}
                          onChange={(e) => updateField('sql_title6', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">個人標語</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_slogan || ''}
                          onChange={(e) => updateField('e_slogan', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">備註</Form.Label>
                        <Form.Control
                          size="sm"
                          as="textarea"
                          rows={2}
                          value={selectedEmployee.e_remark || ''}
                          onChange={(e) => updateField('e_remark', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* 組別資訊 */}
              <Card className="mb-3">
                <Card.Header className="bg-secondary text-white py-2">
                  <h6 className="mb-0">👥 組別資訊</h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">組別</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_set || ''}
                          onChange={(e) => updateField('e_set', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">組別職位</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_set_position || ''}
                          onChange={(e) => updateField('e_set_position', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">稱謂</Form.Label>
                        <Form.Control
                          size="sm"
                          value={selectedEmployee.e_appellation || ''}
                          onChange={(e) => updateField('e_appellation', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">推薦人 ID</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={selectedEmployee.e_recom_uid || ''}
                          onChange={(e) => updateField('e_recom_uid', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-2" />儲存中...</> : '💾 儲存'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 匯入預覽 Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>匯入預覽</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {importPreview && (() => {
            const changes = importPreview.data?.changes || {};
            const summary = importPreview.data?.summary || {};
            const { toUpdate = [], toCreate = [], unchanged = [], errors: errList = [] } = changes;
            return (
              <div>
                <Alert variant="info" className="d-flex justify-content-around text-center mb-3">
                  <div><strong className="text-primary" style={{fontSize:'1.3em'}}>{summary.toUpdate || 0}</strong><br/><small>更新</small></div>
                  <div><strong className="text-success" style={{fontSize:'1.3em'}}>{summary.toCreate || 0}</strong><br/><small>新增</small></div>
                  <div><strong className="text-secondary" style={{fontSize:'1.3em'}}>{summary.unchanged || 0}</strong><br/><small>無變更</small></div>
                  <div><strong className="text-danger" style={{fontSize:'1.3em'}}>{summary.errors || 0}</strong><br/><small>錯誤</small></div>
                  <div><strong style={{fontSize:'1.3em'}}>{summary.total || 0}</strong><br/><small>總筆數</small></div>
                </Alert>

                {/* 更新明細 */}
                {toUpdate.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-primary">📝 更新明細 ({toUpdate.length} 筆)</h6>
                    <Table bordered size="sm" style={{ fontSize: '12px' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{width:'40px'}}>行</th>
                          <th style={{width:'60px'}}>ID</th>
                          <th style={{width:'80px'}}>姓名</th>
                          <th>變更欄位</th>
                          <th>原值</th>
                          <th>新值</th>
                        </tr>
                      </thead>
                      <tbody>
                        {toUpdate.map((item, idx) => {
                          const changeEntries = Object.entries(item.changes || {});
                          return changeEntries.map(([field, val], fIdx) => (
                            <tr key={`upd-${idx}-${fIdx}`}>
                              {fIdx === 0 && (
                                <>
                                  <td rowSpan={changeEntries.length}>{item.rowNum}</td>
                                  <td rowSpan={changeEntries.length}>{item.e_id}</td>
                                  <td rowSpan={changeEntries.length}>{item.name}</td>
                                </>
                              )}
                              <td><Badge bg="warning" text="dark">{field}</Badge></td>
                              <td className="text-danger" style={{textDecoration:'line-through'}}>{val.old != null ? String(val.old) : '(空)'}</td>
                              <td className="text-success fw-bold">{val.new != null ? String(val.new) : '(空)'}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* 新增明細 */}
                {toCreate.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-success">➕ 新增明細 ({toCreate.length} 筆)</h6>
                    <Table bordered size="sm" style={{ fontSize: '12px' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{width:'40px'}}>行</th>
                          <th>姓名</th>
                          <th>手機</th>
                          <th>其他資料</th>
                        </tr>
                      </thead>
                      <tbody>
                        {toCreate.map((item, idx) => {
                          const otherFields = Object.entries(item.data || {})
                            .filter(([k]) => !['ID','姓名','手機'].includes(k) && item.data[k])
                            .map(([k, v]) => `${k}: ${v}`)
                            .join('、');
                          return (
                            <tr key={`new-${idx}`}>
                              <td>{item.rowNum}</td>
                              <td>{item.name}</td>
                              <td>{item.mobile}</td>
                              <td style={{maxWidth:'400px', wordBreak:'break-all'}}>{otherFields || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* 錯誤明細 */}
                {errList.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-danger">❌ 錯誤明細 ({errList.length} 筆)</h6>
                    <Table bordered size="sm" style={{ fontSize: '12px' }}>
                      <thead className="table-danger">
                        <tr>
                          <th style={{width:'40px'}}>行</th>
                          <th style={{width:'100px'}}>姓名</th>
                          <th>錯誤原因</th>
                        </tr>
                      </thead>
                      <tbody>
                        {errList.map((item, idx) => (
                          <tr key={`err-${idx}`}>
                            <td>{item.rowNum}</td>
                            <td>{item.name || '未知'}</td>
                            <td className="text-danger">{item.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* 無變更 (可收合) */}
                {unchanged.length > 0 && (
                  <details className="mb-3">
                    <summary className="text-secondary" style={{ cursor: 'pointer' }}>
                      ⏭ 無變更 ({unchanged.length} 筆) — 點擊展開
                    </summary>
                    <div className="mt-2" style={{ fontSize: '12px', color: '#888' }}>
                      {unchanged.map((item, idx) => (
                        <span key={idx} className="me-2">
                          {item.name}(ID:{item.e_id}){idx < unchanged.length - 1 ? '、' : ''}
                        </span>
                      ))}
                    </div>
                  </details>
                )}

                {toUpdate.length === 0 && toCreate.length === 0 && errList.length === 0 && (
                  <Alert variant="warning">沒有偵測到任何資料變更</Alert>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            取消
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmImport}
            disabled={importing || (
              !(importPreview?.data?.changes?.toUpdate?.length) &&
              !(importPreview?.data?.changes?.toCreate?.length)
            )}
          >
            {importing ? '匯入中...' : '確認匯入'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Lightbox */}
      {isLightboxOpen && (
        <Lightbox
          mainSrc={lightboxImage}
          onCloseRequest={() => setIsLightboxOpen(false)}
        />
      )}
    </Container>
  );
};

export default AllMemberManagement;
